/**
 * People - the contractor roster.
 *
 * ## Two things this screen is careful about
 *
 * **Pay is absent, not null.** `redactUser` deletes `hourlyRateCents` from the
 * payload for anyone not entitled to it, so "the key is missing" and "there is
 * no rate on file" are different facts and are rendered differently. A dash
 * that means both is a screen that quietly teaches people the wrong thing.
 *
 * **Nobody is deleted.** There is no `DELETE /api/users`, on purpose: a person
 * with logged hours is attached to money that has already moved. Deactivating
 * is the whole lifecycle, so it is the prominent control and the copy says
 * what it does.
 */
import type { FormEvent, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import type { ZodError } from 'zod';

import type { Role, UserDto } from '@botguide/shared';
import {
  DEFAULT_WEEKLY_CAPACITY,
  ROLES,
  createPayRateSchema,
  createUserSchema,
  formatCents,
  todayInBusinessZone,
  updateUserSchema,
} from '@botguide/shared';

import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorNote,
  ErrorState,
  Field,
  LoadingScreen,
  PageHeader,
  PersonChip,
  Select,
  Stat,
  Table,
  Tabs,
} from '@/components';
import type { Column } from '@/components';
import { EMPTY, ROLE_LABELS, formatCount, formatPoints } from '@/lib/format';
import {
  useAddPayRate,
  useClients,
  useCreateUser,
  usePayRates,
  useUpdateUser,
  useUsers,
} from '@/lib/queries';
import { needsClient, isPlanned } from '@/lib/role-fields';
import { useCan, useSession } from '@/lib/session';

type RosterTab = 'active' | 'inactive' | 'all';

const ROLE_TONES: Record<Role, 'accent' | 'neutral' | 'warn'> = {
  owner: 'accent',
  contractor: 'neutral',
  client: 'warn',
};

/**
 * Three states, not two.
 *
 * `hidden` is the viewer not being entitled to this person's pay; `unset` is
 * nobody having recorded one yet. Only `shown` ever reaches `formatCents`.
 */
type RateView = { kind: 'hidden' } | { kind: 'unset' } | { kind: 'shown'; cents: number };

function rateOf(user: UserDto): RateView {
  if (!('hourlyRateCents' in user)) return { kind: 'hidden' };
  const cents = user.hourlyRateCents;
  return typeof cents === 'number' ? { kind: 'shown', cents } : { kind: 'unset' };
}

export default function People() {
  const users = useUsers();
  const clients = useClients();
  const { user: viewer } = useSession();

  const canCreate = useCan('create', 'user');
  const canUpdate = useCan('update', 'user');

  const [tab, setTab] = useState<RosterTab>('active');
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<UserDto | null>(null);
  const [pricing, setPricing] = useState<UserDto | null>(null);

  const roster = users.data ?? [];
  const active = roster.filter((person) => person.isActive);
  const inactive = roster.filter((person) => !person.isActive);

  const rows = tab === 'active' ? active : tab === 'inactive' ? inactive : roster;

  /* The column only exists if somebody's rate actually came back. */
  const showRate = roster.some((person) => rateOf(person).kind !== 'hidden');

  const viewerId = viewer?.id ?? null;

  const contractorCapacity = active
    .filter((person) => person.role === 'contractor')
    .reduce((sum, person) => sum + person.weeklyCapacityPoints, 0);

  const columns = useMemo<Column<UserDto>[]>(() => {
    const list: Column<UserDto>[] = [
      {
        key: 'person',
        header: 'Person',
        cell: (person) => (
          <div className="flex min-w-0 flex-col gap-1.5">
            <PersonChip name={person.name} id={person.id} secondary={person.title} size="sm" />
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone={ROLE_TONES[person.role]} size="sm">
                {ROLE_LABELS[person.role]}
              </Badge>
              {person.isActive ? null : (
                <Badge tone="neutral" size="sm" dot>
                  Inactive
                </Badge>
              )}
            </div>
            <span className="truncate font-mono text-[0.6875rem] text-subtle">{person.email}</span>
          </div>
        ),
      },
      {
        key: 'capacity',
        header: 'Capacity',
        align: 'right',
        numeric: true,
        width: '7.5rem',
        cell: (person) => (
          <span className="whitespace-nowrap">
            {formatPoints(person.weeklyCapacityPoints)}
            <span className="text-subtle"> pts/wk</span>
          </span>
        ),
      },
    ];

    if (showRate) {
      list.push({
        key: 'rate',
        header: 'Rate',
        align: 'right',
        numeric: true,
        secondary: true,
        width: '8.5rem',
        cell: (person) => {
          const rate = rateOf(person);
          if (rate.kind === 'shown') {
            return <span className="whitespace-nowrap">{formatCents(rate.cents)}/h</span>;
          }
          return (
            <span
              className="text-subtle"
              title={rate.kind === 'hidden' ? 'Not visible to your account' : 'No rate on file'}
            >
              {rate.kind === 'hidden' ? EMPTY : 'No rate'}
            </span>
          );
        },
      });
    }

    if (canUpdate) {
      list.push({
        key: 'actions',
        header: <span className="sr-only">Actions</span>,
        align: 'right',
        width: '1%',
        cell: (person) => {
          // Deactivating yourself locks you out of the screen you did it on.
          const self = person.id === viewerId;
          return (
            <div className="flex items-center justify-end gap-1.5">
              {/*
                Only a contractor has a pay rate - an owner and a client have
                nothing to price - so the control is absent rather than
                present and pointless for the other two roles.
              */}
              {person.role === 'contractor' ? (
                <Button size="sm" variant="ghost" className="px-2" onClick={() => setPricing(person)}>
                  Rate<span className="sr-only"> for {person.name}</span>
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" className="px-2" onClick={() => setEditing(person)}>
                Edit<span className="sr-only"> {person.name}</span>
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={self}
                title={self ? 'You cannot deactivate yourself.' : undefined}
                onClick={() => setDeactivating(person)}
              >
                {person.isActive ? 'Deactivate' : 'Reactivate'}
                <span className="sr-only"> {person.name}</span>
              </Button>
            </div>
          );
        },
      });
    }

    return list;
  }, [showRate, canUpdate, viewerId]);

  return (
    <>
      <PageHeader
        title="People"
        description="Everyone at BotGuide is a contractor. Weekly capacity is what the Capacity screen plans against."
        actions={
          canCreate ? (
            <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
              Add person
            </Button>
          ) : null
        }
        toolbar={
          roster.length > 0 ? (
            <div className="flex flex-col gap-4">
              <Card padding="md" className="rise">
                <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3">
                  <Stat label="On the roster" value={roster.length} hint={`${active.length} active`} />
                  <Stat
                    label="Contractors"
                    value={active.filter((person) => person.role === 'contractor').length}
                    hint="Active only"
                  />
                  <Stat
                    label="Weekly capacity"
                    value={formatPoints(contractorCapacity)}
                    hint="Story points across active contractors"
                  />
                </div>
              </Card>

              <Tabs
                aria-label="Roster filter"
                value={tab}
                onChange={setTab}
                items={[
                  { value: 'active', label: 'Active', count: active.length },
                  { value: 'inactive', label: 'Inactive', count: inactive.length },
                  { value: 'all', label: 'Everyone', count: roster.length },
                ]}
              />
            </div>
          ) : null
        }
      />

      {users.isPending ? (
        <LoadingScreen label="Loading the roster" />
      ) : users.error ? (
        <ErrorState error={users.error} onRetry={() => void users.refetch()} />
      ) : roster.length === 0 ? (
        <EmptyState
          icon="people"
          title="Nobody on the roster yet"
          description="Add the contractors who do the work. Their weekly capacity is what the Capacity screen plans against."
          action={
            canCreate ? (
              <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
                Add the first person
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <Table
            caption="The BotGuide roster"
            columns={columns}
            rows={rows}
            rowKey={(person) => person.id}
            rowClassName={(person) => (person.isActive ? undefined : 'opacity-65')}
            empty={
              <EmptyState
                bare
                icon="people"
                title={tab === 'inactive' ? 'Nobody is deactivated' : 'Nobody here'}
                description={
                  tab === 'inactive'
                    ? 'Everyone on the roster is currently active.'
                    : 'Everyone on the roster has been deactivated.'
                }
              />
            }
          />
          <p className="mt-3 text-[0.8125rem] text-subtle">
            People are deactivated, never deleted — hours already logged stay attached to the work
            they were logged against.
          </p>
        </>
      )}

      {creating ? (
        <CreatePersonDialog
          clients={(clients.data ?? []).map((client) => ({ value: client.id, label: client.name }))}
          onClose={() => setCreating(false)}
        />
      ) : null}

      {editing ? (
        <EditPersonDialog
          key={editing.id}
          person={editing}
          clients={(clients.data ?? []).map((client) => ({ value: client.id, label: client.name }))}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {deactivating ? (
        <ActivationDialog
          key={deactivating.id}
          person={deactivating}
          onClose={() => setDeactivating(null)}
        />
      ) : null}

      {pricing ? (
        <PayRateDialog key={pricing.id} person={pricing} onClose={() => setPricing(null)} />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Pay rate                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What somebody is paid, from a date.
 *
 * ## Why this is its own dialog and not a field on Edit
 *
 * ADR-0006: a rate carries `effective_from` and no end date, so changing what
 * somebody is paid is **appending a row**, not editing one. A text input on
 * the edit form would say the opposite — and the edit dialog already tells the
 * truth ("what they are paid is not an edit to a roster row"). This is the
 * "elsewhere" that sentence promised and, until F11 was found, did not exist.
 *
 * ## Why the date matters more than it looks
 *
 * Hours are priced at read time from the rate in force on the day they were
 * worked, so backdating a rate prices work that has already been approved.
 * That is the point — a contractor onboarded before anyone set their rate has
 * hours sitting at "—" that this fixes — but it is also how somebody
 * accidentally re-prices an invoiced month, so the form says so out loud.
 */
function PayRateDialog({ person, onClose }: { person: UserDto; onClose: () => void }) {
  const history = usePayRates(person.id);
  const add = useAddPayRate(person.id);

  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState(todayInBusinessZone());
  const [errors, setErrors] = useState<Record<string, string>>({});

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    // Dollars in, integer cents out. Money never travels as a float, and
    // `Math.round` is what stops 35.35 arriving as 3534.9999999999995.
    const dollars = Number(amount);
    if (!Number.isFinite(dollars) || dollars < 0 || amount.trim() === '') {
      setErrors({ amount: 'Enter an hourly rate, like 35 or 35.50' });
      return;
    }

    const parsed = createPayRateSchema.omit({ userId: true }).safeParse({
      hourlyRateCents: Math.round(dollars * 100),
      effectiveFrom: from,
    });

    if (!parsed.success) {
      setErrors(firstIssues(parsed.error));
      return;
    }

    setErrors({});
    add.mutate(parsed.data, {
      onSuccess: () => {
        setAmount('');
        setErrors({});
      },
    });
  }

  const rows = history.data ?? [];

  return (
    <Dialog
      open
      onClose={onClose}
      title="Pay rate"
      description={`${person.name} — appended, never edited`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" type="submit" form={RATE_FORM_ID} loading={add.isPending}>
            Add rate
          </Button>
        </>
      }
    >
      <form id={RATE_FORM_ID} className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        {add.error ? <ErrorNote error={add.error} /> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Hourly rate"
            required
            inputMode="decimal"
            placeholder="35.00"
            hint="US dollars per hour."
            value={amount}
            error={errors.amount ?? null}
            onChange={(event) => setAmount(event.target.value)}
          />
          <Field
            label="Effective from"
            type="date"
            required
            hint="The first day this rate applies."
            value={from}
            error={errors.effectiveFrom ?? null}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>

        <p className="rounded-md border border-line bg-sunken px-3.5 py-3 text-[0.8125rem] text-muted">
          Hours are priced by the rate in force on the day they were worked, so a date in the past
          re-prices work already logged — including hours that were approved with no rate at all.
          Nothing after this date is touched until you add another rate.
        </p>

        <div className="flex flex-col gap-2">
          <h3 className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-subtle">
            History
          </h3>
          {history.isPending ? (
            <p className="text-[0.8125rem] text-subtle">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-[0.8125rem] text-subtle">
              No rate has ever been set. Their approved hours are earning nothing until one is.
            </p>
          ) : (
            <dl className="flex flex-col gap-2 rounded-md border border-line bg-sunken px-3.5 py-3 text-[0.8125rem]">
              {rows.map((rate) => (
                <Fixed key={rate.id} term={rate.effectiveFrom}>
                  <span className="font-mono tnum">{formatCents(rate.hourlyRateCents)}/h</span>
                </Fixed>
              ))}
            </dl>
          )}
        </div>
      </form>
    </Dialog>
  );
}

const RATE_FORM_ID = 'pay-rate-form';

/* -------------------------------------------------------------------------- */
/* Create                                                                     */
/* -------------------------------------------------------------------------- */

const CREATE_FORM_ID = 'create-person';
const EDIT_FORM_ID = 'edit-person';

const ROLE_OPTIONS = ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }));

/** Blank means "use the schema default"; anything else must parse as a number. */
function capacityValue(raw: string): number | undefined {
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : Number(trimmed);
}

/** The first message per field. A second message on the same field is noise. */
function firstIssues(error: ZodError): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !(key in fields)) fields[key] = issue.message;
  }
  return fields;
}

function CreatePersonDialog({
  clients,
  onClose,
}: {
  clients: { value: string; label: string }[];
  onClose: () => void;
}) {
  const create = useCreateUser();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState<Role>('contractor');
  const [clientId, setClientId] = useState('');
  const [capacity, setCapacity] = useState(String(DEFAULT_WEEKLY_CAPACITY));
  const [errors, setErrors] = useState<Record<string, string>>({});

  /*
   * `createUserSchema` refuses a client user with no clientId AND a non-client
   * user with one. Rather than let somebody submit and collect a 400, the
   * picker only exists for the role that may have it, and the other roles send
   * an explicit null.
   */
  const showClient = needsClient(role);

  /*
   * Weekly capacity is what the Capacity screen plans against, and
   * `routes/capacity.ts` filters that screen to `role = 'contractor'`. So the
   * field is meaningful for exactly one role; for the other two it collects a
   * number nothing will ever read. `docs/FINDINGS.md` F15 and F27.
   */
  const planned = isPlanned(role);

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    const parsed = createUserSchema.safeParse({
      email,
      name,
      title: title.trim() === '' ? null : title,
      role,
      clientId: showClient ? (clientId === '' ? null : clientId) : null,
      /*
       * Zero, not the schema's default of five. The field is not shown for a
       * role nothing plans, so any number sent would be one nobody chose - and
       * five would render as "5 pts/wk" on the People table beside people who
       * genuinely have none. Zero is the honest reading of "has no capacity".
       */
      weeklyCapacityPoints: planned ? capacityValue(capacity) : 0,
    });

    if (!parsed.success) {
      setErrors(firstIssues(parsed.error));
      return;
    }

    setErrors({});
    create.mutate(parsed.data, { onSuccess: onClose });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a person"
      description="They sign in with this address — there is no password to set."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={CREATE_FORM_ID}
            variant="primary"
            iconRight="check"
            loading={create.isPending}
          >
            Add to the roster
          </Button>
        </>
      }
    >
      <form id={CREATE_FORM_ID} className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        {create.error ? <ErrorNote error={create.error} /> : null}

        <Field
          label="Email address"
          type="email"
          autoComplete="off"
          required
          autoFocus
          placeholder="name@botgui.de"
          value={email}
          error={errors.email ?? null}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Field
          label="Name"
          required
          value={name}
          error={errors.name ?? null}
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          label="Title"
          placeholder="Engineer"
          hint="Shown beside their name across the app."
          value={title}
          error={errors.title ?? null}
          onChange={(event) => setTitle(event.target.value)}
        />

        <Select
          label="Role"
          required
          options={ROLE_OPTIONS}
          value={role}
          error={errors.role ?? null}
          onChange={(event) => setRole(event.target.value as Role)}
        />

        {showClient ? (
          <Select
            label="Client"
            required
            placeholder={clients.length === 0 ? 'No clients yet' : 'Choose a client'}
            options={clients}
            value={clientId}
            error={errors.clientId ?? null}
            hint="A client user only ever sees this client's work."
            onChange={(event) => setClientId(event.target.value)}
          />
        ) : null}

        {/*
          Absent rather than disabled, matching the Rate button on the list
          behind this dialog: a control that cannot act is not shown.
        */}
        {planned ? (
          <Field
            label="Weekly capacity"
            type="number"
            inputMode="decimal"
            min={0}
            max={40}
            step={0.5}
            required
            hint="Story points a normal week holds. One point is one day; five is a full week."
            className="sm:max-w-40"
            value={capacity}
            error={errors.weeklyCapacityPoints ?? null}
            onChange={(event) => setCapacity(event.target.value)}
          />
        ) : null}
      </form>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Edit                                                                       */
/* -------------------------------------------------------------------------- */

function EditPersonDialog({
  person,
  clients,
  onClose,
}: {
  person: UserDto;
  clients: { value: string; label: string }[];
  onClose: () => void;
}) {
  const update = useUpdateUser();
  const { user: viewer } = useSession();

  const [email, setEmail] = useState(person.email);
  const [role, setRole] = useState<Role>(person.role);
  const [clientId, setClientId] = useState(person.clientId ?? '');
  const [name, setName] = useState(person.name);
  const [title, setTitle] = useState(person.title ?? '');
  const [capacity, setCapacity] = useState(String(person.weeklyCapacityPoints));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const rate = rateOf(person);

  /*
   * The twin of the roster's "You cannot deactivate yourself" (People.tsx:181).
   * The API refuses this too - this only stops you getting as far as a
   * rejected save.
   */
  const self = viewer?.id === person.id;

  const planned = isPlanned(role);
  const showClient = needsClient(role);

  function onSubmit(event: FormEvent) {
    event.preventDefault();

    const parsed = updateUserSchema.safeParse({
      email,
      name,
      title: title.trim() === '' ? null : title,
      /*
       * Role is omitted entirely on your own row: the API refuses a self role
       * change, and sending the unchanged value would be noise.
       */
      ...(self ? {} : { role, clientId: showClient ? clientId || null : null }),
      /*
       * Zero for anyone the Capacity screen does not plan, rather than leaving
       * whatever is stored alone. This dialog is how a *client* acquired a
       * capacity of 1 while F27 was open.
       */
      weeklyCapacityPoints: planned ? capacityValue(capacity) : 0,
    });

    if (!parsed.success) {
      setErrors(firstIssues(parsed.error));
      return;
    }

    setErrors({});
    update.mutate({ id: person.id, patch: parsed.data }, { onSuccess: onClose });
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit person"
      description={person.name}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={EDIT_FORM_ID}
            variant="primary"
            iconRight="check"
            loading={update.isPending}
          >
            Save changes
          </Button>
        </>
      }
    >
      <form id={EDIT_FORM_ID} className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        {update.error ? <ErrorNote error={update.error} /> : null}

        <Field
          label="Name"
          required
          autoFocus
          value={name}
          error={errors.name ?? null}
          onChange={(event) => setName(event.target.value)}
        />
        <Field
          label="Title"
          value={title}
          error={errors.title ?? null}
          onChange={(event) => setTitle(event.target.value)}
        />

        <Field
          label="Email address"
          type="email"
          required
          hint="Sign-in codes go here. Changing it means the old address stops working."
          value={email}
          error={errors.email ?? null}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Select
          label="Role"
          required
          options={ROLE_OPTIONS}
          value={role}
          disabled={self}
          error={errors.role ?? null}
          hint={self ? 'You cannot change your own role.' : undefined}
          onChange={(event) => setRole(event.target.value as Role)}
        />

        {showClient ? (
          <Select
            label="Client"
            required
            placeholder={clients.length === 0 ? 'No clients yet' : 'Choose a client'}
            options={clients}
            value={clientId}
            error={errors.clientId ?? null}
            hint="A client user only ever sees this client's work."
            onChange={(event) => setClientId(event.target.value)}
          />
        ) : null}

        {planned ? (
          <Field
            label="Weekly capacity"
            type="number"
            inputMode="decimal"
            min={0}
            max={40}
            step={0.5}
            required
            hint="Changing this moves every week on the Capacity screen, past and future."
            className="sm:max-w-40"
            value={capacity}
            error={errors.weeklyCapacityPoints ?? null}
            onChange={(event) => setCapacity(event.target.value)}
          />
        ) : null}

        <dl className="flex flex-col gap-2 rounded-md border border-line bg-sunken px-3.5 py-3 text-[0.8125rem]">
          {rate.kind === 'shown' ? (
            <Fixed term="Pay rate">
              <span className="font-mono tnum">{formatCents(rate.cents)}/h</span>
            </Fixed>
          ) : null}
          <p className="pt-1 text-subtle">
            Pay is set on the Rate dialog — what somebody is paid is a dated record, not a field.
          </p>
        </dl>
      </form>
    </Dialog>
  );
}

function Fixed({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-20 shrink-0 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-subtle">
        {term}
      </dt>
      <dd className="min-w-0 truncate text-muted">{children}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Activate / deactivate                                                      */
/* -------------------------------------------------------------------------- */

function ActivationDialog({ person, onClose }: { person: UserDto; onClose: () => void }) {
  const update = useUpdateUser();
  const deactivating = person.isActive;

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={deactivating ? 'Deactivate this person?' : 'Bring this person back?'}
      description={person.name}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                { id: person.id, patch: { isActive: !person.isActive } },
                { onSuccess: onClose },
              )
            }
          >
            {deactivating ? 'Deactivate' : 'Reactivate'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-muted">
        {update.error ? <ErrorNote error={update.error} /> : null}

        {deactivating ? (
          <>
            <p>
              <span className="font-medium text-ink">{person.name}</span> drops off capacity
              planning and stops appearing as someone new work can be assigned to.
            </p>
            <p>
              Nothing is removed. Their {formatCount(person.weeklyCapacityPoints, 'point')} of
              weekly capacity stops being counted, and every hour they have logged stays attached to
              the work it was logged against. This is reversible.
            </p>
            <p className="text-subtle">
              There is no way to delete a person, and that is deliberate — hours already approved
              have been paid.
            </p>
          </>
        ) : (
          <p>
            <span className="font-medium text-ink">{person.name}</span> goes back on the roster, can
            be assigned work again, and their{' '}
            {formatCount(person.weeklyCapacityPoints, 'point')} of weekly capacity counts towards
            planning from the current week onwards.
          </p>
        )}
      </div>
    </Dialog>
  );
}
